import { ArrowRight } from 'lucide-react'
import { contentPath, type ContentEntry } from '../lib/content'

/**
 * One customer in a grid: the square testimonial card on top (the real branded
 * PNG when a story has one, otherwise a matching CSS quote-card built from its
 * frontmatter), then an industry eyebrow, the title, and a read link. Used by
 * the "more stories" rail on each story page (the /customers index has its own
 * masonry markup). The square aspect matches the PNGs' native ratio, so the
 * real cards are shown whole rather than cropped.
 */
export function CustomerCard({
  entry,
  titleAs: TitleTag = 'h2',
}: {
  entry: ContentEntry
  titleAs?: 'h2' | 'h3'
}) {
  const { meta } = entry
  return (
    <a href={contentPath(entry)} className="group flex flex-col">
      <div className="aspect-[714/756] overflow-hidden rounded-2xl border border-ink-100 bg-ink-950 ring-1 ring-inset ring-white/5 transition duration-500 group-hover:-translate-y-1 group-hover:shadow-[0_24px_60px_-24px_rgba(37,99,235,0.45)]">
        {meta.card ? (
          <img
            src={meta.card}
            alt={`${meta.company ?? meta.title} on LangWatch`}
            loading="lazy"
            className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
          />
        ) : (
          <QuoteCard company={meta.company ?? meta.title} quote={meta.quote} />
        )}
      </div>
      {meta.industry && (
        <div className="mt-3.5 font-mono text-[10px] uppercase tracking-[0.18em] text-ink-400">
          {meta.industry}
        </div>
      )}
      <TitleTag className="mt-1.5 text-[16px] font-semibold leading-[1.35] text-ink-900 transition-colors group-hover:text-ink-600">
        {meta.title}
      </TitleTag>
      <div className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-ink-600">
        Read story
        <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
      </div>
    </a>
  )
}

/** Fallback card for a customer without a branded PNG: brand gradient, a faint
 * quote-mark, the company wordmark, and the pull quote. Sized and weighted to
 * sit beside the real testimonial cards without looking like a gap. */
function QuoteCard({ company, quote }: { company: string; quote?: string }) {
  return (
    <div
      className="relative flex h-full flex-col justify-between overflow-hidden p-5 text-paper"
      style={{
        backgroundImage: 'linear-gradient(150deg, #1f2a86 0%, #762c04 52%, #d65209 100%)',
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-12 -right-3 select-none font-serif text-[220px] leading-none text-white/10"
      >
        “
      </div>
      <div className="relative text-[19px] font-semibold tracking-[-0.01em]">{company}</div>
      {quote && (
        <blockquote className="relative line-clamp-6 text-[13.5px] leading-[1.5] text-paper/90">
          "{quote}"
        </blockquote>
      )}
    </div>
  )
}
